/* import __COLOCATED_TEMPLATE__ from './scim.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';

import type IntlService from 'ember-intl/services/intl';
import type ScimManagementService from 'embercom/services/scim-management-service';

import type { ScimGroup, ScimArgs } from './scim/types';
import { ScimGroupState } from './scim/types';
import {
  SCIM_TABLE_COLUMNS,
  CHANGES_REVIEW_TABLE_COLUMNS,
  getUserInitials,
  createRoleDropdownList,
  getGroupStateDisplayName,
  getGroupStateColor,
} from './scim/utils';
import { SCIM_TRANSLATION_KEYS } from './scim/constants';

export default class Scim extends Component<ScimArgs> {
  @service declare intl: IntlService;
  @service declare scimManagementService: ScimManagementService;

  constructor(owner: unknown, args: ScimArgs) {
    super(owner, args);
    this.loadData();
  }

  get availableRoles() {
    return this.args.roles || [];
  }

  get scimGroupsTableColumns() {
    return SCIM_TABLE_COLUMNS(this.intl);
  }

  get changesReviewTableColumns() {
    return CHANGES_REVIEW_TABLE_COLUMNS(this.intl);
  }

  get canEditScim(): boolean {
    return this.scimManagementService.canEditScim;
  }

  get hasUnsavedChanges(): boolean {
    return this.scimManagementService.changesState.hasChanges();
  }

  get scimGroups(): ScimGroup[] {
    return this.scimManagementService.currentState.groups;
  }

  get sortedScimGroups(): ScimGroup[] {
    return this.scimManagementService.changesState.sortedGroups;
  }

  get hasGroups(): boolean {
    return this.scimGroups.length > 0;
  }

  get hasCurrentGroupChanges(): boolean {
    return this.scimManagementService.selectedGroup
      ? Boolean(
          this.scimManagementService.changesState.roleChanges[
            this.scimManagementService.selectedGroup.id
          ],
        )
      : false;
  }

  get modalRoleDropdownList() {
    if (!this.scimManagementService.selectedGroup) {
      return [];
    }
    return createRoleDropdownList(this.availableRoles, this.intl, this.updateRoleInModal);
  }

  get currentModalRoleName(): string {
    if (!this.scimManagementService.selectedGroup) {
      return '';
    }

    let group = this.scimManagementService.selectedGroup;
    let roleChange = this.scimManagementService.changesState.roleChanges[group.id];
    let effectiveRoleId = roleChange !== undefined ? roleChange : group.role_id;

    return this.getRoleNameById(effectiveRoleId);
  }

  get pendingChangesForReview() {
    return this.scimManagementService.getPendingChangesForReview();
  }

  get hasIncludedChanges(): boolean {
    return this.scimManagementService.hasIncludedChanges();
  }

  get groupChanges() {
    return this.scimManagementService.changesState.roleChanges;
  }

  get showDetailsModal(): boolean {
    return this.scimManagementService.showDetailsModal;
  }

  get showApplyChangesModal(): boolean {
    return this.scimManagementService.showApplyChangesModal;
  }

  get showRoleChangeConfirmationModal(): boolean {
    return this.scimManagementService.showRoleChangeConfirmationModal;
  }

  get selectedGroupForDetails(): ScimGroup | null {
    return this.scimManagementService.selectedGroup;
  }

  get isLoadingGroupDetails(): boolean {
    return this.scimManagementService.isLoadingGroupDetails;
  }

  get roleChangeConfirmationData() {
    return this.scimManagementService.confirmationData;
  }

  get roleChangeConfirmationCurrentRole(): string {
    return this.roleChangeConfirmationData?.currentRole || '';
  }

  get roleChangeConfirmationNewRole(): string {
    return this.roleChangeConfirmationData?.newRole || '';
  }

  get roleChangeConfirmationGroupName(): string {
    return this.roleChangeConfirmationData?.groupName || '';
  }

  get isLoading(): boolean {
    return this.scimManagementService.isLoading;
  }

  get isLoadingUserGroups(): boolean {
    return this.scimManagementService.isLoadingUserGroups;
  }

  get isCurrentUserMemberOfSelectedGroup(): boolean {
    return this.scimManagementService.isCurrentUserMemberOfGroup(this.selectedGroupForDetails);
  }

  get isCurrentUserAffectedByPendingChanges(): boolean {
    return this.scimManagementService.isCurrentUserAffectedByPendingChanges();
  }

  @action
  getCurrentRoleForGroup(groupId: string | number): number | null {
    let group = this.scimGroups.find((g) => g.id === groupId.toString());
    let roleChange = this.scimManagementService.changesState.roleChanges[groupId.toString()];
    return roleChange !== undefined ? roleChange : group?.role_id ?? null;
  }

  @action
  getCurrentRoleNameForGroup(groupId: string | number): string {
    let roleId = this.getCurrentRoleForGroup(groupId);
    return this.getRoleNameById(roleId);
  }

  private getRoleNameById(roleId: number | null): string {
    let role = this.availableRoles.find((r) => r.id === roleId?.toString());
    return role?.name || this.intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLE_ASSIGNED);
  }

  @action
  getRoleDropdownList(groupId: string | number) {
    return createRoleDropdownList(this.availableRoles, this.intl, (roleId) =>
      this.updateGroupRole(groupId, roleId),
    );
  }

  @action
  updateGroupRole(groupId: string | number, roleId: number | null) {
    this.scimManagementService.updateRole(groupId.toString(), roleId);
  }

  @action
  getUserInitials(user: Parameters<typeof getUserInitials>[0]) {
    return getUserInitials(user);
  }

  @action
  getGroupStateDisplayName(state: string | null) {
    return getGroupStateDisplayName(state, this.intl);
  }

  @action
  getGroupStateColor(state: string | null) {
    return getGroupStateColor(state);
  }

  @action
  getGroupPriority(group: ScimGroup): string {
    if (group.state === ScimGroupState.INACTIVE) {
      return '-';
    }
    return String(this.sortedScimGroups.findIndex((g) => g.id === group.id) + 1);
  }

  @action
  reorderGroups(reorderedGroups: ScimGroup[]) {
    if (!this.canEditScim) {
      return;
    }
    this.scimManagementService.reorderGroups(reorderedGroups);
  }

  @action
  async openGroupDetails(group: ScimGroup) {
    this.scimManagementService.openGroupDetails(group);

    try {
      await this.scimManagementService.loadGroupDetails(parseInt(group.id, 10));
    } catch (error) {
      console.error('Failed to load group details:', error);
    }
  }

  @action
  closeDetailsModal() {
    this.scimManagementService.closeGroupDetails();
  }

  @action
  openRoleChangeConfirmationModal() {
    this.scimManagementService.openRoleChangeConfirmation('single');
  }

  @action
  closeRoleChangeConfirmationModal() {
    this.scimManagementService.closeRoleChangeConfirmation();
  }

  @action
  async confirmRoleChange() {
    let source = this.roleChangeConfirmationData?.source;
    this.scimManagementService.closeRoleChangeConfirmation();

    if (source === 'bulk') {
      await this.handleBulkChangesConfirmed();
    } else {
      await this.handleSingleGroupChangeConfirmed();
    }
  }

  @action
  updateRoleInModal(roleId: number | null) {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    this.updateGroupRole(this.scimManagementService.selectedGroup.id, roleId);
  }

  @action
  async applyCurrentGroupChanges() {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    let groupId = this.scimManagementService.selectedGroup.id;
    let hasChanges = Boolean(this.scimManagementService.changesState.roleChanges[groupId]);

    if (!hasChanges) {
      return;
    }

    if (this.scimManagementService.shouldShowConfirmation()) {
      this.scimManagementService.openRoleChangeConfirmation('single');
      return;
    }

    try {
      await this.scimManagementService.applyChanges();
      this.scimManagementService.closeGroupDetails();
    } catch (error) {
      console.error('Failed to apply group changes:', error);
    }
  }

  @action
  openApplyChangesModal() {
    if (!this.canEditScim || !this.hasUnsavedChanges) {
      return;
    }
    this.scimManagementService.openApplyChangesModal();
  }

  @action
  closeApplyChangesModal() {
    this.scimManagementService.closeApplyChangesModal();
  }

  @action
  async confirmApplyChanges() {
    if (!this.canEditScim || !this.hasIncludedChanges) {
      this.closeApplyChangesModal();
      return;
    }

    if (this.scimManagementService.shouldShowConfirmation()) {
      this.scimManagementService.openRoleChangeConfirmation('bulk');
      return;
    }

    await this.handleConfirmationApproved(() => this.closeApplyChangesModal());
  }

  @action
  toggleGroupChange(groupId: string): void {
    this.scimManagementService.toggleGroupChange(groupId);
  }

  @action
  toggleOrderChanges(): void {
    this.scimManagementService.toggleOrderChanges();
  }

  @action
  applyChanges() {
    this.openApplyChangesModal();
  }

  @action
  cancelChanges() {
    this.scimManagementService.resetChanges();
  }

  private async loadData() {
    await this.scimManagementService.loadData(this.availableRoles);
  }

  private async handleSingleGroupChangeConfirmed() {
    await this.handleConfirmationApproved(() => this.scimManagementService.closeGroupDetails());
  }

  private async handleBulkChangesConfirmed() {
    await this.handleConfirmationApproved(() =>
      this.scimManagementService.closeApplyChangesModal(),
    );
  }

  private async handleConfirmationApproved(onSuccess: () => void) {
    try {
      await this.scimManagementService.applyChanges();
      onSuccess();
    } catch (error) {
      console.error('Failed to apply changes:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::Scim': typeof Scim;
    'new-settings/workspace/teammates/scim': typeof Scim;
  }
}
